import { BaseService } from '../base/BaseService';
import { ServiceError } from '../base/ServiceError';
import type { QueryOptions, ServiceResponse } from '../base/ServiceTypes';

import type { Database } from '@/integrations/supabase/types';

type BookmarkRow = Database['public']['Tables']['bookmarked_posts']['Row'];
type BookmarkInsert = Database['public']['Tables']['bookmarked_posts']['Insert'];
type _BookmarkUpdate = Database['public']['Tables']['bookmarked_posts']['Update'];
type _PostRow = Database['public']['Tables']['social_posts']['Row'];

export type BookmarkWithPost = BookmarkRow;

export interface CreateBookmarkInput {
  postId: string;
  notes?: string;
}

export class BookmarkService extends BaseService {
  private static instance: BookmarkService;

  private constructor() {
    super({
      defaultTTL: 5 * 60 * 1000, // 5 minutes
      enableCache: true,
      enableRetry: true,
    });
  }

  public static getInstance(): BookmarkService {
    if (!BookmarkService.instance) {
      BookmarkService.instance = new BookmarkService();
    }
    return BookmarkService.instance;
  }

  /**
   * Get all bookmarks for the current user
   */
  async getBookmarks(options?: QueryOptions): Promise<ServiceResponse<BookmarkWithPost[]>> {
    return this.execute(async () => {
      const userId = await this.ensureAuthenticated();
      const cacheKey = `bookmarks-${userId}`;

      return this.executeWithCache(
        cacheKey,
        async () => {
          // Enhanced query with error handling and validation
          let query = this.client.from('bookmarked_posts').select('*').eq('user_id', userId);

          // Add ordering with fallback
          try {
            query = this.buildQuery(query, {
              orderBy: 'created_at',
              orderDirection: 'desc',
              ...options,
            });
          } catch (orderError) {
            this.log.warn('Failed to apply ordering, using default', orderError);
            // Use created_at for ordering
            query = query.order('created_at', { ascending: false });
          }

          const { data, error } = await query;

          if (error) {
            // Handle various database errors gracefully (similar to ProductService)
            if (error.code === '42P01' || error.code === 'PGRST116') {
              this.log.warn('bookmarked_posts table does not exist - returning empty array');
              return [];
            }
            // Handle RLS (Row Level Security) and permission errors
            if (
              error.code === 'PGRST301' ||
              error.message?.includes('permission denied') ||
              error.message?.includes('RLS')
            ) {
              this.log.warn('Bookmark query blocked by RLS - returning empty array');
              return [];
            }
            // Handle 400 Bad Request errors which could be schema mismatches
            if (error.message?.includes('Bad Request') || error.status === 400) {
              this.log.warn(
                'Bookmark query failed with Bad Request - returning empty array',
                error,
              );
              return [];
            }
            throw error;
          }

          return data || [];
        },
        options,
      );
    });
  }

  /**
   * Get a single bookmark by ID
   */
  async getBookmark(id: string): Promise<ServiceResponse<BookmarkWithPost | null>> {
    return this.execute(async () => {
      const userId = await this.ensureAuthenticated();
      const cacheKey = `bookmark-${id}`;

      return this.executeWithCache(cacheKey, async () => {
        const { data, error } = await this.client
          .from('bookmarked_posts')
          .select('*')
          .eq('id', id)
          .eq('user_id', userId)
          .single();

        if (error) {
          if (error.code === 'PGRST116') {
            throw ServiceError.notFound('Bookmark');
          }
          throw error;
        }

        return data;
      });
    });
  }

  /**
   * Check if a post is bookmarked
   */
  async isPostBookmarked(postId: string): Promise<ServiceResponse<boolean>> {
    return this.execute(async () => {
      const userId = await this.ensureAuthenticated();

      const { data, error } = await this.client
        .from('bookmarked_posts')
        .select('id')
        .eq('user_id', userId)
        .eq('post_id', postId)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      return !!data;
    });
  }

  /**
   * Get bookmark by post ID
   */
  async getBookmarkByPostId(postId: string): Promise<ServiceResponse<BookmarkWithPost | null>> {
    return this.execute(async () => {
      const userId = await this.ensureAuthenticated();

      const { data, error } = await this.client
        .from('bookmarked_posts')
        .select('*')
        .eq('user_id', userId)
        .eq('post_id', postId)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      return data;
    });
  }

  /**
   * Create a new bookmark
   */
  async createBookmark(input: CreateBookmarkInput): Promise<ServiceResponse<BookmarkRow>> {
    return this.execute(async () => {
      const userId = await this.ensureAuthenticated();

      // Check if already bookmarked
      const existingResponse = await this.isPostBookmarked(input.postId);
      if (existingResponse.data) {
        throw ServiceError.validationError('This post is already bookmarked');
      }

      // bookmarked_posts stores only the reference (user_id, post_id, notes).
      // It has NO platform/post_data columns, so the previous enrichment query
      // selected non-existent social_posts columns (platform/post_content/
      // image_url/posted_at/likes/comments/shares) — failing PostgREST — and the
      // insert wrote non-existent platform/post_data columns, so bookmark
      // creation failed every time. Display surfaces fetch fresh post data by
      // post_id instead of relying on a stored snapshot.
      const bookmarkData: BookmarkInsert = {
        user_id: userId,
        post_id: input.postId,
        notes: input.notes,
      };

      const { data, error } = await this.client
        .from('bookmarked_posts')
        .insert(bookmarkData)
        .select()
        .single();

      if (error) throw error;

      // Clear cache
      this.clearCache(`bookmarks-${userId}`);

      return data;
    }, 'Failed to create bookmark');
  }

  /**
   * Update bookmark notes
   */
  async updateBookmarkNotes(
    id: string,
    notes: string | null,
  ): Promise<ServiceResponse<BookmarkRow>> {
    return this.execute(async () => {
      const userId = await this.ensureAuthenticated();

      const { data, error } = await this.client
        .from('bookmarked_posts')
        .update({ notes })
        .eq('id', id)
        .eq('user_id', userId)
        .select()
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          throw ServiceError.notFound('Bookmark');
        }
        throw error;
      }

      // Clear relevant caches
      this.clearCache(`bookmark-${id}`);
      this.clearCache(`bookmarks-${userId}`);

      return data;
    }, 'Failed to update bookmark');
  }

  /**
   * Delete a bookmark
   */
  async deleteBookmark(id: string): Promise<ServiceResponse<void>> {
    return this.execute(async () => {
      const userId = await this.ensureAuthenticated();

      const { error } = await this.client
        .from('bookmarked_posts')
        .delete()
        .eq('id', id)
        .eq('user_id', userId);

      if (error) {
        if (error.code === 'PGRST116') {
          throw ServiceError.notFound('Bookmark');
        }
        throw error;
      }

      // Clear relevant caches
      this.clearCache(`bookmark-${id}`);
      this.clearCache(`bookmarks-${userId}`);
    }, 'Failed to delete bookmark');
  }

  /**
   * Delete bookmark by post ID
   */
  async deleteBookmarkByPostId(postId: string): Promise<ServiceResponse<void>> {
    return this.execute(async () => {
      const userId = await this.ensureAuthenticated();

      const { error } = await this.client
        .from('bookmarked_posts')
        .delete()
        .eq('post_id', postId)
        .eq('user_id', userId);

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      // Clear cache
      this.clearCache(`bookmarks-${userId}`);
    });
  }

  /**
   * Toggle bookmark for a post
   */
  async toggleBookmark(
    postId: string,
    notes?: string,
  ): Promise<
    ServiceResponse<{
      bookmarked: boolean;
      bookmark?: BookmarkRow;
    }>
  > {
    return this.execute(async () => {
      // Check if already bookmarked
      const existingResponse = await this.getBookmarkByPostId(postId);

      if (existingResponse.data) {
        // Remove bookmark
        await this.deleteBookmark(existingResponse.data.id);
        return { bookmarked: false };
      } else {
        // Create bookmark
        const createResponse = await this.createBookmark({ postId, notes });
        return {
          bookmarked: true,
          bookmark: createResponse.data!,
        };
      }
    });
  }

  /**
   * Get bookmarks with filters - Enhanced with better error handling
   */
  async getBookmarksWithFilters(
    filters: {
      search?: string;
      startDate?: string;
      endDate?: string;
      hasNotes?: boolean;
    },
    options?: QueryOptions,
  ): Promise<ServiceResponse<BookmarkWithPost[]>> {
    return this.execute(async () => {
      const userId = await this.ensureAuthenticated();

      try {
        let query = this.client.from('bookmarked_posts').select('*').eq('user_id', userId);

        // Apply filters with error handling
        if (filters.hasNotes === true) {
          query = query.not('notes', 'is', null);
        } else if (filters.hasNotes === false) {
          query = query.is('notes', null);
        }

        if (filters.startDate) {
          // Use created_at for date filtering
          query = query.gte('created_at', filters.startDate);
        }

        if (filters.endDate) {
          query = query.lte('created_at', filters.endDate);
        }

        // Apply query options with error handling
        try {
          query = this.buildQuery(query, {
            orderBy: 'created_at',
            orderDirection: 'desc',
            ...options,
          });
        } catch (buildError) {
          this.log.warn('Failed to build query with created_at', buildError);
          query = this.buildQuery(query, {
            orderBy: 'created_at',
            orderDirection: 'desc',
            ...options,
          });
        }

        const { data, error } = await query;

        if (error) {
          // Enhanced error handling for common issues
          if (error.message?.includes('column "created_at" does not exist')) {
            this.log.warn('created_at column missing', error);

            // Retry with created_at
            let retryQuery = this.client.from('bookmarked_posts').select('*').eq('user_id', userId);

            if (filters.hasNotes === true) {
              retryQuery = retryQuery.not('notes', 'is', null);
            } else if (filters.hasNotes === false) {
              retryQuery = retryQuery.is('notes', null);
            }

            if (filters.startDate) {
              retryQuery = retryQuery.gte('created_at', filters.startDate);
            }

            if (filters.endDate) {
              retryQuery = retryQuery.lte('created_at', filters.endDate);
            }

            retryQuery = this.buildQuery(retryQuery, {
              orderBy: 'created_at',
              orderDirection: 'desc',
              ...options,
            });

            const { data: retryData, error: retryError } = await retryQuery;
            if (retryError) throw retryError;

            let results = retryData || [];

            // Apply search filter
            if (filters.search) {
              const searchLower = filters.search.toLowerCase();
              results = results.filter(
                (bookmark) =>
                  bookmark.notes?.toLowerCase().includes(searchLower) ||
                  JSON.stringify(bookmark.post_data || {})
                    .toLowerCase()
                    .includes(searchLower),
              );
            }

            return results;
          }

          throw error;
        }

        let results = data || [];

        // Apply search filter in memory if needed
        if (filters.search) {
          const searchLower = filters.search.toLowerCase();
          results = results.filter(
            (bookmark) =>
              bookmark.notes?.toLowerCase().includes(searchLower) ||
              JSON.stringify(bookmark.post_data || {})
                .toLowerCase()
                .includes(searchLower),
          );
        }

        return results;
      } catch (error) {
        // Final fallback: return empty array instead of throwing
        this.log.error('BookmarkService.getBookmarksWithFilters failed', error);

        // Log the error details for debugging
        if (error instanceof Error) {
          this.log.error('Error details', null, {
            message: error.message,
            userId,
            filters,
          });
        }

        // Return empty array as graceful fallback
        return [];
      }
    });
  }

  /**
   * Get bookmark count
   */
  async getBookmarkCount(): Promise<ServiceResponse<number>> {
    return this.execute(async () => {
      const userId = await this.ensureAuthenticated();

      const { count, error } = await this.client
        .from('bookmarked_posts')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId);

      if (error) {
        // Handle various database errors gracefully (similar to ProductService)
        if (error.code === '42P01' || error.code === 'PGRST116') {
          this.log.warn('bookmarked_posts table does not exist - returning 0 count');
          return 0;
        }
        // Handle RLS (Row Level Security) and permission errors
        if (
          error.code === 'PGRST301' ||
          error.message?.includes('permission denied') ||
          error.message?.includes('RLS')
        ) {
          this.log.warn('Bookmark count query blocked by RLS - returning 0');
          return 0;
        }
        // Handle 400 Bad Request errors which could be schema mismatches
        if (error.message?.includes('Bad Request') || error.status === 400) {
          this.log.warn('Bookmark count query failed with Bad Request - returning 0', error);
          return 0;
        }
        throw error;
      }

      return count || 0;
    });
  }

  /**
   * Bulk create bookmarks
   */
  async bulkCreateBookmarks(
    bookmarks: CreateBookmarkInput[],
  ): Promise<ServiceResponse<BookmarkRow[]>> {
    return this.execute(async () => {
      const userId = await this.ensureAuthenticated();

      // Check for existing bookmarks
      const postIds = bookmarks.map((b) => b.postId);
      const { data: existing } = await this.client
        .from('bookmarked_posts')
        .select('post_id')
        .eq('user_id', userId)
        .in('post_id', postIds);

      const existingPostIds = new Set((existing || []).map((e) => e.post_id));

      // Filter out already bookmarked posts
      const newBookmarks = bookmarks.filter((b) => !existingPostIds.has(b.postId));

      if (newBookmarks.length === 0) {
        return [];
      }

      // Create bookmarks
      const bookmarksWithUser = newBookmarks.map((b) => ({
        user_id: userId,
        post_id: b.postId,
        notes: b.notes,
      }));

      const { data, error } = await this.client
        .from('bookmarked_posts')
        .insert(bookmarksWithUser)
        .select();

      if (error) throw error;

      // Clear cache
      this.clearCache(`bookmarks-${userId}`);

      return data || [];
    }, 'Failed to create bookmarks');
  }

  /**
   * Clear all bookmark caches
   */
  clearAllCaches(): void {
    const cacheStore = (BaseService as any).cacheStore as Map<string, any>;
    for (const key of cacheStore.keys()) {
      if (key.startsWith('bookmark')) {
        this.clearCache(key);
      }
    }
  }
}

// Export singleton instance
export const bookmarkService = BookmarkService.getInstance();
